import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";

import { api } from "@/lib/api";
import * as queue from "@/lib/offline-queue";
import { startReminderScheduler } from "@/lib/notifications";
import { statsApi } from "@/lib/stats-api";
import Header from "@/components/RefClick/Header";
import SummaryBar from "@/components/RefClick/SummaryBar";
import LinkItem from "@/components/RefClick/LinkItem";
import useShuffledAvatars from "@/hooks/useShuffledAvatars";
import EmptyState from "@/components/RefClick/EmptyState";
import LinkModal from "@/components/RefClick/LinkModal";
import LinkDetail from "@/components/RefClick/LinkDetail";
import DeleteConfirm from "@/components/RefClick/DeleteConfirm";
import SettingsModal from "@/components/RefClick/SettingsModal";
import ShortcutsOverlay from "@/components/RefClick/ShortcutsOverlay";
// Lazy-load the IP check modal: ~17 KB of JSX + framer-motion variants
// that only matter once the user opens the panel. Keeps the critical-
// path bundle focused on the dashboard itself (Feb-2026 refactor).
const IpCheckModal = lazy(() => import("@/components/RefClick/IpCheckModal"));
import SortableLinks from "@/components/RefClick/SortableLinks";

/**
 * Top-level page for RefClick.
 *
 * Owns:
 *   - Server data (links + summary), refreshed after every mutation and
 *     lightly polled every 30 s (paused on visibilitychange).
 *   - UI preferences (theme + layout), persisted to localStorage.
 *     Default theme = "sunset-amber".
 *   - Modal states: Add/Edit modal, Delete confirmation, Link Detail
 *     slide-over.
 *   - Celebration bookkeeping (fires exactly once per (link, day)
 *     false → true target transition).
 */

const PREF_KEYS = { theme: "refclick.theme", layout: "refclick.layout" };
const DEFAULT_THEME = "sunset-amber";
const DEFAULT_LAYOUT = "list"; // 'list' | 'grid'
const POLL_MS = 30_000;

function loadPref(key, fallback) {
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}
function savePref(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private browsing: swallow */
  }
}

/**
 * One-time migration: when the default theme was flipped from 'coral' to
 * 'sunset-amber', any existing user's browser still carried the old value
 * in localStorage. Wipe it once per browser so the new default takes
 * effect, then respect whatever the user picks going forward.
 */
function runThemeMigrationOnce() {
  try {
    const flag = "refclick.migrated.v2";
    if (!window.localStorage.getItem(flag)) {
      window.localStorage.removeItem(PREF_KEYS.theme);
      window.localStorage.setItem(flag, "1");
    }
  } catch {
    /* no-op */
  }
}

export default function Dashboard() {
  const [links, setLinks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [theme, setTheme] = useState(() => {
    runThemeMigrationOnce();
    return loadPref(PREF_KEYS.theme, DEFAULT_THEME);
  });
  const [layout, setLayout] = useState(() => loadPref(PREF_KEYS.layout, DEFAULT_LAYOUT));

  const [modal, setModal] = useState({ open: false, mode: "create", link: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [ipcheckOpen, setIpcheckOpen] = useState(false);
  // Quick-check in SummaryBar can request the modal pre-filled with
  // an IP it just checked — passed down to IpCheckModal via `prefillIp`.
  const [ipcheckPrefill, setIpcheckPrefill] = useState("");
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // The currently-open detail link, DERIVED from the freshest links list
  // so its stats (today_count, last_click, heatmap) stay live even while
  // the user is editing its metadata.
  const detailLink = detailId ? links.find((l) => l.id === detailId) : null;

  const [earningsByLink, setEarningsByLink] = useState({});

  // Feb-2026 avatar shuffle. Runs a one-per-minute reshuffle across
  // the current link list, honoring visibility + reduced-motion (see
  // hook). Keying on link IDs (rather than the objects themselves)
  // means the map survives every refresh() call — only add/delete
  // trigger a top-up assignment.
  const avatarAssignment = useShuffledAvatars(links.map((l) => l.id));
  const [topEarner, setTopEarner] = useState(null);
  const metStateRef = useRef({});
  const isFirstLoad = useRef(true);

  /* -------------------------------------------------------------------- */
  /* Data fetching                                                         */
  /* -------------------------------------------------------------------- */

  const refresh = useCallback(async () => {
    try {
      const [linksData, summaryData] = await Promise.all([api.listLinks(), api.summary()]);
      // Trust boundary — coerce server array response so a legacy shape
      // (dict/string/null) can't crash the .map render sites downstream.
      setLinks(Array.isArray(linksData) ? linksData : []);
      setSummary(summaryData);
      // Fire-and-forget the earnings summary — it's not critical for the
      // dashboard to render, just for the per-card $ chip and the
      // "top earner this month" ribbon.
      statsApi
        .earningsSummary()
        .then((s) => {
          // Card $ chip shows CURRENT MONTH only — the number resets
          // visually at midnight on the 1st of each Dhaka month while
          // historical data stays intact (viewable via the detail
          // panel's all-time figure and the Stats page's month browser).
          const map = {};
          const rows = Array.isArray(s?.per_link) ? s.per_link : [];
          for (const row of rows) {
            map[row.link_id] = row.total_usd_this_month || 0;
          }
          setEarningsByLink(map);

          // Top earner: highest total_usd_this_month > 0. Ties → the first
          // one (per_link is already sorted by sort_order server-side).
          // .slice() to avoid mutating the array returned by the API.
          const contenders = rows
            .filter((r) => (r.total_usd_this_month || 0) > 0)
            .slice();
          if (contenders.length === 0) {
            setTopEarner(null);
          } else {
            contenders.sort(
              (a, b) => b.total_usd_this_month - a.total_usd_this_month,
            );
            setTopEarner({
              link_id: contenders[0].link_id,
              total_this_month: contenders[0].total_usd_this_month,
              usd_per_click_30d: contenders[0].usd_per_click_30d,
            });
          }
        })
        .catch(() => {
          /* leave stale — chip/ribbon just won't refresh this cycle */
        });
      setError(null);
      return { linksData, summaryData };
    } catch (e) {
      setError("Couldn't reach the server. Retrying…");
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  // 30 s poll while the tab is visible; paused on visibilitychange.
  useEffect(() => {
    let timer = null;
    const start = () => {
      stop();
      timer = window.setInterval(() => {
        if (document.visibilityState === "visible") refresh().catch(() => {});
      }, POLL_MS);
    };
    const stop = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        refresh().catch(() => {});
        start();
      } else {
        stop();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refresh]);

  /* -------------------------------------------------------------------- */
  /* Celebration: fire only on false → true transition per link/day        */
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    const nextMet = {};
    for (const l of links) nextMet[l.id] = l.target_met;

    if (isFirstLoad.current) {
      metStateRef.current = nextMet;
      isFirstLoad.current = false;
      return;
    }

    const newlyMet = links.filter((l) => l.target_met && !metStateRef.current[l.id]);
    if (newlyMet.length > 0) fireCelebration(newlyMet[0].name);

    metStateRef.current = nextMet;
  }, [links]);

  /* -------------------------------------------------------------------- */
  /* Preference persistence                                                */
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    savePref(PREF_KEYS.theme, theme);
  }, [theme]);

  useEffect(() => {
    savePref(PREF_KEYS.layout, layout);
  }, [layout]);

  /* -------------------------------------------------------------------- */
  /* Offline queue — drain on reconnect + on first mount if online.        */
  /* -------------------------------------------------------------------- */
  const drainNow = useCallback(async () => {
    if (!navigator.onLine) return;
    if (!(await queue.count())) return;
    window.dispatchEvent(
      new CustomEvent("refclick:sync", { detail: true }),
    );
    try {
      await queue.drainQueue(async (row) => {
        if (row.kind === "click") await api._clickDirect(row.link_id);
        else if (row.kind === "undo") await api._undoDirect(row.link_id);
      });
      queue.notify();
      // Only refresh if any work actually happened (guard against extra hits)
      await refresh();
    } finally {
      window.dispatchEvent(
        new CustomEvent("refclick:sync", { detail: false }),
      );
    }
  }, [refresh]);

  useEffect(() => {
    drainNow();
    const onOnline = () => drainNow();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [drainNow]);

  /* -------------------------------------------------------------------- */
  /* Reminders — load persisted settings once and start the JS scheduler.  */
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;
    statsApi
      .getSettings()
      .then((s) => {
        if (cancelled) return;
        if (s?.reminders_enabled) {
          startReminderScheduler({
            enabled: true,
            times: s.reminder_times || [],
          });
        }
      })
      .catch(() => {
        /* ignore — user can configure via Settings */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* -------------------------------------------------------------------- */
  /* Keyboard shortcuts (desktop). 1-9 → +1 Nth; Shift+1-9 → -1 Nth.       */
  /* "?" toggles the overlay. Ignored while typing in inputs / modals.     */
  /* -------------------------------------------------------------------- */
  const isTypingTarget = (el) => {
    if (!el) return false;
    const tag = (el.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return true;
    if (el.isContentEditable) return true;
    return false;
  };
  const anyModalOpen =
    modal.open ||
    !!deleteTarget ||
    !!detailId ||
    settingsOpen ||
    shortcutsOpen;

  useEffect(() => {
    const onKey = (e) => {
      if (isTypingTarget(e.target)) return;
      // "?" toggles the overlay. Accept both the interpreted key ("?")
      // AND the raw Shift+/ combo (event.code === "Slash") so that
      // synthesised keyboard events in headless browsers — which don't
      // always translate Shift+/ into "?" — still hit this branch.
      const isQuestionMark =
        e.key === "?" ||
        (e.shiftKey && e.code === "Slash") ||
        (e.shiftKey && e.key === "/");
      if (isQuestionMark && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setShortcutsOpen((v) => !v);
        return;
      }
      if (anyModalOpen) return;
      // Digits 1-9 target the Nth visible link (index N-1).
      const digit =
        e.key >= "1" && e.key <= "9" ? parseInt(e.key, 10) : null;
      if (digit == null) return;
      const idx = digit - 1;
      const target = links[idx];
      if (!target) return;
      e.preventDefault();
      if (e.shiftKey) handleUndo(target);
      else handleClick(target);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [links, anyModalOpen]);

  const hasKeyboardHint = useMemo(() => {
    if (typeof window === "undefined") return false;
    // Coarse touch pointer → treat as touch device; hide the number badge.
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
      return false;
    return true;
  }, []);

  /* -------------------------------------------------------------------- */
  /* Mutations                                                             */
  /* -------------------------------------------------------------------- */
  const handleReorder = useCallback(
    async (newIds) => {
      // Optimistic swap first so the drop animation feels instant.
      const indexOf = new Map(newIds.map((id, i) => [id, i]));
      const prev = links;
      const reordered = [...links].sort(
        (a, b) => (indexOf.get(a.id) ?? 0) - (indexOf.get(b.id) ?? 0),
      );
      setLinks(reordered);
      try {
        const resp = await api.reorderLinks(newIds);
        if (Array.isArray(resp)) setLinks(resp);
      } catch (e) {
        // Revert on failure.
        setLinks(prev);
        toast.error("Couldn't save new order — reverted");
        throw e;
      }
    },
    [links],
  );

  const handleClick = async (link) => {
    try {
      const res = await api.click(link.id);
      if (res && res.queued) {
        toast.message("Queued", {
          description: "Offline — this click will sync when you're back.",
        });
        queue.notify();
      } else {
        await refresh();
      }
    } catch {
      toast.error("Couldn't record that click. Try again?");
    }
  };

  const handleUndo = async (link) => {
    // Feb-2026 fix (rapid +1/−1 swallow):
    // We used to short-circuit here on `link.today_count <= 0`, but
    // that prop is a stale closure — during a rapid +1/−1 burst the
    // +1's optimistic UI hasn't yet round-tripped through refresh(),
    // so the stale 0 caused the first −1 to be silently dropped even
    // though the server had just moved 0 → 1. The server's undo path
    // is already atomic + capped at 0 (conditional filter in Mongo,
    // no crash, returns the current state), so letting every press
    // fire is correct AND cheap. If the row IS actually at 0 offline,
    // the queue swallows the noop identically.
    try {
      const res = await api.undo(link.id);
      if (res && res.queued) {
        toast.message("Undo queued", {
          description: "Offline — this will sync when you're back.",
        });
        queue.notify();
      } else {
        await refresh();
      }
    } catch {
      toast.error("Undo failed. Try again?");
    }
  };

  const handleSubmitLink = async (payload) => {
    try {
      if (modal.mode === "edit" && modal.link) {
        await api.updateLink(modal.link.id, payload);
        toast.success("Link updated");
      } else {
        await api.createLink(payload);
        toast.success("Link added");
      }
      setModal({ open: false, mode: "create", link: null });
      await refresh();
    } catch (e) {
      const msg =
        e?.response?.data?.detail?.[0]?.msg ||
        e?.response?.data?.detail ||
        "Please check the fields and try again.";
      toast.error(typeof msg === "string" ? msg : "Validation error");
      throw e;
    }
  };

  const handleSaveDetail = async (payload) => {
    if (!detailId) return;
    try {
      // Preserve every server-required field the sheet doesn't edit.
      const existing = links.find((l) => l.id === detailId);
      const merged = {
        name: payload.name,
        url: payload.url,
        daily_target: payload.daily_target,
        custom_fields: payload.custom_fields || [],
      };
      await api.updateLink(detailId, merged);
      toast.success("Saved");
      await refresh();
      // Keep the sheet open so the user can keep editing; if they want to
      // close it they use the X. This matches the compact-workflow spirit.
      // But close it if they're leaving no work behind.
      if (!existing || existing.name === payload.name) {
        // no-op — keep open
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail?.[0]?.msg ||
        e?.response?.data?.detail ||
        "Save failed. Check the fields.";
      toast.error(typeof msg === "string" ? msg : "Save failed");
      throw e;
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteLink(deleteTarget.id);
      toast.success("Link removed");
      setDeleteTarget(null);
      if (detailId === deleteTarget.id) setDetailId(null);
      await refresh();
    } catch {
      toast.error("Delete failed. Try again?");
    }
  };

  /* -------------------------------------------------------------------- */
  /* Render                                                                */
  /* -------------------------------------------------------------------- */
  return (
    <div
      className="ambient-bg relative min-h-screen w-full text-text-main"
      data-testid="refclick-dashboard"
    >
      <main className="relative z-10 mx-auto flex w-full max-w-4xl flex-col px-2 py-3 sm:px-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="glass-panel overflow-hidden rounded-2xl"
        >
          <Header
            theme={theme}
            onThemeChange={setTheme}
            layout={layout}
            onLayoutChange={setLayout}
            onAdd={() => setModal({ open: true, mode: "create", link: null })}
            onOpenSettings={() => setSettingsOpen(true)}
            onOpenIpCheck={() => setIpcheckOpen(true)}
            onCleared={() => refresh()}
            hasLinks={links.length > 0}
          />

          <SummaryBar
            summary={summary}
            error={error}
            loading={loading}
            onOpenIpCheck={(ip) => {
              setIpcheckPrefill(ip || "");
              setIpcheckOpen(true);
            }}
          />

          <div className="px-2 pb-4 pt-2 sm:px-5 sm:pb-6 sm:pt-3">
            {loading ? (
              <div className="py-10 text-center text-sm text-text-muted">
                Loading…
              </div>
            ) : links.length === 0 ? (
              <EmptyState
                onAdd={() =>
                  setModal({ open: true, mode: "create", link: null })
                }
              />
            ) : (
              <div
                className={
                  layout === "grid"
                    ? "grid grid-cols-1 gap-3 sm:grid-cols-2"
                    : "flex flex-col gap-1.5"
                }
                data-testid="link-list-container"
              >
                <SortableLinks
                  items={links}
                  variant={layout === "grid" ? "card" : "row"}
                  onReorder={handleReorder}
                  renderItem={(link, idx, dragHandle) => (
                    <motion.div
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{
                        duration: 0.35,
                        delay: idx * 0.04,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                    >
                      <LinkItem
                        link={link}
                        avatarIndex={avatarAssignment[link.id]}
                        allLinks={links}
                        earningsUsd={earningsByLink[link.id] || 0}
                        isTopEarner={topEarner?.link_id === link.id}
                        topEarnerInfo={
                          topEarner?.link_id === link.id ? topEarner : null
                        }
                        variant={layout === "grid" ? "card" : "row"}
                        shortcutHint={hasKeyboardHint && idx < 9 ? idx + 1 : null}
                        dragHandle={dragHandle}
                        onClick={() => handleClick(link)}
                        onUndo={() => handleUndo(link)}
                        onOpen={(id) => setDetailId(id)}
                        onEdit={() =>
                          setModal({ open: true, mode: "edit", link })
                        }
                        onDelete={() => setDeleteTarget(link)}
                        onEarningsChanged={() => refresh()}
                      />
                    </motion.div>
                  )}
                />
              </div>
            )}
          </div>
        </motion.div>

        <p
          className="mx-auto mt-5 max-w-full truncate text-center text-[11px] text-text-muted/60"
          data-testid="footer-note"
        >
          Resets at midnight (Dhaka)
        </p>
      </main>

      <LinkModal
        open={modal.open}
        mode={modal.mode}
        link={modal.link}
        onClose={() => setModal({ open: false, mode: "create", link: null })}
        onSubmit={handleSubmitLink}
      />

      <LinkDetail
        key={detailId /* remount on link switch → fresh form state */}
        link={detailLink}
        open={!!detailId}
        onOpenChange={(v) => !v && setDetailId(null)}
        onSave={handleSaveDetail}
        onDelete={(l) => setDeleteTarget(l)}
        onEarningsChanged={() => refresh()}
      />

      <DeleteConfirm
        link={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />

      <SettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onCleared={() => refresh()}
      />

      <ShortcutsOverlay
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
      />

      {/* IP checker is lazy-loaded — Suspense fallback is null because
          the modal only renders when `open` is true, and the user won't
          notice the ~200 ms script fetch (gated behind an explicit click). */}
      {ipcheckOpen && (
        <Suspense fallback={null}>
          <IpCheckModal
            open={ipcheckOpen}
            onOpenChange={(v) => {
              setIpcheckOpen(v);
              if (!v) setIpcheckPrefill("");
            }}
            prefillIp={ipcheckPrefill}
            onOpenSettings={() => {
              // Switch to Settings — the user clicked a "needs API key"
              // hint on an inactive provider chip. We close the IP check
              // modal and open Settings; the IP-checker-keys section
              // auto-scrolls into view via a hash target.
              try {
                history.replaceState(null, "", "#ipcheck-keys");
              } catch {
                /* ignore history API restrictions */
              }
              setIpcheckOpen(false);
              setSettingsOpen(true);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Confetti helper — theme-color aware, short, respects reduced-motion.     */
/* ------------------------------------------------------------------------ */
function fireCelebration(linkName) {
  const accentRgb = getComputedStyle(document.documentElement)
    .getPropertyValue("--accent")
    .trim()
    .split(/\s+/)
    .map(Number);
  const [r, g, b] = accentRgb.length === 3 ? accentRgb : [255, 140, 0];
  const primary = `rgb(${r}, ${g}, ${b})`;
  const soft = `rgba(${r}, ${g}, ${b}, 0.65)`;

  confetti({
    particleCount: 60,
    spread: 68,
    startVelocity: 42,
    ticks: 120,
    origin: { y: 0.35 },
    colors: [primary, soft, "#ffffff"],
    scalar: 0.9,
    disableForReducedMotion: true,
  });

  toast.success(`${linkName} — daily target hit`, { duration: 1800 });
}
